import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { SrdSearchIndex } from '@roll1/shared/srd/types';
import { formatCr } from '@roll1/shared/srd/mapping';
import { monsterKindNames, ruMonsterAliases, ruMonsterName } from '@roll1/shared/srd/ru-names';
import { searchFilter } from '../../lib/search/engine';
import { useSrdIndex } from '../../lib/search/srdIndex';
import type { TranslationKeys } from '../../i18n/types.js';
import { useI18n } from '../../i18n';
import { Input } from '../ui/input';
import { IconButton } from '../ui/icon-button';
import { Plus } from 'lucide-react';

/**
 * F-3: the SRD bestiary in the Library — a folded section after the records. The names are the search index's
 * (/api/srd/index, shared with Ctrl+K) and the matching is the app's one matcher (group M): typos, Russian names
 * and words («дракон»), best first. No card and no spawn buttons: a monster is dragged onto the Studio map
 * (dragType «srd-monster», the same payload as at the table), where the drop spawns it on THAT map.
 */

/** A monster of the SRD name index (GET /api/srd/index): what the list, the drag and «＋ на карту» need. */
export type BestiaryMonster = SrdSearchIndex['monsters'][number];

/**
 * The names a monster is found by: its SRD name, its Russian name, the Russian words of its name, and its type and
 * subtype in English and Russian («undead» / «нежить», «demon» / «демон») — the old API search matched the type too.
 */
export function bestiaryNames(m: Pick<BestiaryMonster, 'name' | 'ru' | 'type' | 'subtype'>): string[] {
  return [m.name, ...(m.ru === undefined ? [] : [m.ru]), ...ruMonsterAliases(m.name), ...monsterKindNames(m.type, m.subtype)];
}

/** The name a monster has on a table of this language: «Гоблин» on a Russian one when the list has it, else the SRD name. */
export function monsterLabel(name: string, locale: string): string {
  return locale === 'ru' ? ruMonsterName(name) ?? name : name;
}

/** What «＋ на карту» needs from the host: the map it adds to (undefined — none open) and the add itself. */
export interface BestiarySpawn {
  readonly targetName: string | undefined;
  readonly canAdd: boolean;
  /** GK-2: `count` of them, each in its own free cell. */
  readonly onAdd: (monster: BestiaryMonster, count: number) => void;
}

const MAX_COUNT = 20;
/** Rows listed at once; «Showing N of M — narrow the search» says the rest. */
const SHOWN = 50;
function clampCount(raw: string): number {
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) ? Math.min(MAX_COUNT, Math.max(1, n)) : 1;
}

export function StudioBestiary(props: {
  readonly t: (key: keyof TranslationKeys) => string;
  readonly spawn?: BestiarySpawn;
  /** Inside the rail's «Бестиарий» section (sidebar polish П1): the section header folds it — no toggle of its own. */
  readonly embedded?: boolean;
}): JSX.Element {
  const { t, spawn, embedded = false } = props;
  const { locale } = useI18n();
  const [open, setOpen] = useState<boolean>(embedded);
  const [search, setSearch] = useState<string>('');
  // «＋ на карту ×N»: how many are put on the map by one press.
  const [count, setCount] = useState<number>(1);
  // The names come once (the search index); the matching is the app's one matcher — typos, Russian names, best first.
  // Nothing is fetched until the section is opened.
  const index = useSrdIndex(open);
  const found = useMemo((): readonly BestiaryMonster[] => searchFilter(search, index?.monsters ?? [], bestiaryNames), [index, search]);
  const monsters = found.slice(0, SHOWN);
  const total = found.length;

  return (
    <div className="studio-rail__bestiary mt-1 flex flex-col gap-0.5" data-testid="studio-bestiary">
      {embedded ? null : (
      <button
        type="button"
        className="studio-rail__subheading studio-rail__group-toggle flex items-center gap-1.5 rounded px-1.5 py-0.5 text-left text-meta font-semibold text-fg hover:bg-panel-2/70"
        aria-expanded={open}
        onClick={(): void => setOpen((prev: boolean): boolean => !prev)}
      >
        <span className="w-2.5 flex-none text-label text-dim" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="min-w-0 flex-1 truncate">{t('studio.rail.bestiary')}</span>
        {open ? (
          <span className="studio-rail__group-count flex-none font-mono text-label font-normal text-dim">{total}</span>
        ) : null}
      </button>
      )}
      {open ? (
        <>
          <div className="mx-1 flex items-center gap-1.5">
            <Input
              type="search"
              className="studio-rail__search min-w-0 flex-1"
              aria-label={t('studio.rail.bestiarySearch')}
              placeholder={t('studio.rail.bestiarySearch')}
              value={search}
              onChange={(e): void => setSearch(e.target.value)}
            />
            {spawn === undefined ? null : (
              <label className="flex flex-none items-center gap-1 text-meta text-dim">
                <span aria-hidden="true">×</span>
                <Input
                  type="number"
                  min={1}
                  max={MAX_COUNT}
                  className="w-14 pointer-coarse:w-16"
                  data-testid="bestiary-count"
                  aria-label={t('studio.bestiary.count')}
                  title={t('studio.bestiary.count')}
                  value={count}
                  onChange={(e): void => setCount(clampCount(e.target.value))}
                />
              </label>
            )}
          </div>
          <ul className="studio-rail__bestiary-list flex max-h-72 flex-col overflow-y-auto">
            {monsters.map(
              (m: BestiaryMonster): JSX.Element => (
                <li key={m.slug} className="flex min-w-0 items-center">
                  <button
                    type="button"
                    className="studio-rail__entity flex min-w-0 flex-1 cursor-grab items-center gap-1.5 rounded px-2.5 py-1 text-left text-body-s text-fg hover:bg-panel-2 hover:text-fg active:cursor-grabbing"
                    title={`${monsterLabel(m.name, locale)}${monsterLabel(m.name, locale) !== m.name ? ` (${m.name})` : ''}`}
                    draggable
                    onDragStart={(e): void => {
                      // The payload the table's SRD panel makes: the canvas drop (drop:monster) reads the slug.
                      e.dataTransfer.setData('text/plain', m.name);
                      e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({
                          dragType: 'srd-monster',
                          slug: m.slug,
                          name: monsterLabel(m.name, locale),
                          type: m.type,
                          size: m.size,
                          hp: m.hp,
                          ac: m.ac,
                          cr: m.cr,
                          tokenSize: m.tokenSize,
                        }),
                      );
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {monsterLabel(m.name, locale)}
                      {monsterLabel(m.name, locale) !== m.name ? <span className="ml-1.5 text-label text-dim">{m.name}</span> : null}
                    </span>
                    <span className="flex-none text-label text-dim">CR {formatCr(m.cr)}</span>
                  </button>
                  {spawn === undefined ? null : (
                    <IconButton
                      size="sm"
                      className="min-h-5 min-w-5 flex-none disabled:pointer-events-auto disabled:cursor-not-allowed disabled:text-dim"
                      icon={<Plus size={14} aria-hidden="true" />}
                      label={`${t('studio.bestiary.add')}: ${m.name}`}
                      hint={spawn.targetName === undefined ? t('studio.bestiary.noMap') : t('studio.bestiary.addTo').replace('{map}', spawn.targetName)}
                      // Keep the existing destination hint on the icon action, including when it is disabled.
                      data-testid="bestiary-add"
                      disabled={!spawn.canAdd}
                      onClick={(): void => spawn.onAdd(m, count)}
                    />
                  )}
                </li>
              ),
            )}
          </ul>
          {monsters.length === 0 && /[а-яё]/i.test(search) ? (
            <p className="px-2 text-pill text-dim" data-testid="bestiary-en-hint">{t('studio.bestiary.ruHint')}</p>
          ) : null}
          {total > monsters.length ? (
            <p className="px-2 text-pill text-dim">
              {t('studio.bestiary.more').replace('{shown}', String(monsters.length)).replace('{total}', String(total))}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default StudioBestiary;
